import React, { Suspense } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { RouteWithLayout, AnonymousRoute } from './components';
import LoadingFallback from './components/LoadingFallback';
import { Main as MainLayout, Minimal as MinimalLayout, MinimalPdf as MinimalPdfLayout, MainPayment as MainPaymentLayout } from './layouts';
import { useMediaQuery } from '@material-ui/core';
import { Advertising } from 'views/Admin/components';
// Chunk loading error handler
const retryChunkLoad = (fn, retriesLeft = 3, interval = 1000) => {
    return new Promise((resolve, reject) => {
        fn()
            .then(resolve)
            .catch((error) => {
                if (retriesLeft === 0) {
                    reject(error);
                    return;
                }

                // If it's a ChunkLoadError, try to reload the page
                if (error.name === 'ChunkLoadError') {
                    console.warn(`ChunkLoadError detected, retrying... (${retriesLeft} attempts left)`);
                    setTimeout(() => {
                        if (retriesLeft === 1) {
                            // Last attempt - reload the page to get fresh chunks
                            window.location.reload();
                            return;
                        }
                        retryChunkLoad(fn, retriesLeft - 1, interval).then(resolve, reject);
                    }, interval);
                } else {
                    reject(error);
                }
            });
    });
};

// Enhanced lazy loading with retry logic
const lazyWithRetry = (importFunc) => {
    return React.lazy(() => retryChunkLoad(importFunc));
};

// Lazy load components with retry logic for chunk loading errors
const DashboardView = lazyWithRetry(() => import('./views/Dashboard'));
const SignInView = lazyWithRetry(() => import('./views/SignIn'));
const OAuthSelectCompanyView = lazyWithRetry(() => import('./views/OAuthSelectCompany'));
const EmployeeListView = lazyWithRetry(() => import('./views/EmployeeList'));
const EmployeeDetailsView = lazyWithRetry(() => import('./views/EmployeeDetails'));
const EmployeesPayrollView = lazyWithRetry(() => import('./views/EmployeesPayroll'));
const EmployeePayrollView = lazyWithRetry(() => import('./views/EmployeePayroll'));
const EmployeeLogHoursView = lazyWithRetry(() => import('./views/EmployeeLogHours'));
const EmployeeRoutesView = lazyWithRetry(() => import('./views/EmployeeRoutes'));

const AddCustomerView = lazyWithRetry(() => import('./views/AddCustomer'));
const ForgotPasswordView = lazyWithRetry(() => import('./views/ForgotPassword'));
const ResetPasswordView = lazyWithRetry(() => import('./views/ResetPassword'));
const CustomerView = lazyWithRetry(() => import('./views/CustomerList'));
const CustomerDetailsView = lazyWithRetry(() => import('./views/CustomerDetails'));
const CustomerJobsView = lazyWithRetry(() => import('./views/CustomerJobs'));
const CustomerBillingView = lazyWithRetry(() => import('./views/CustomerBilling'));

const SchedulesView = lazyWithRetry(() => import('./views/Schedules'));
const RecurringRoutesView = lazyWithRetry(() => import('./views/RecurringRoutes'));
const TemplateJobEditorView = lazyWithRetry(() => import('./views/Schedules/components/templates/TemplateJobEditor'));
const RoutesView = lazyWithRetry(() => import('./views/Routes'));
const Routes2View = lazyWithRetry(() => import('./views/Routes2'));
const RoutesMobileView = lazyWithRetry(() => import('./views/RoutesMobile'));

const AddUserView = lazyWithRetry(() => import('./views/AddUser'));
const NotFoundView = lazyWithRetry(() => import('./views/NotFound'));
const JobCompletionView = lazyWithRetry(() => import('./views/JobCompletion'));
const ExpiredSchedulesView = lazyWithRetry(() => import('./views/ExpiredSchedules'));
const ReportView = lazyWithRetry(() => import('./views/Report'));
const AdminView = lazyWithRetry(() => import('./views/Admin'));
const CommunicationCenterView = lazyWithRetry(() => import('./views/CommunicationCenter'));
const CommunicationCenterSettingsView = lazyWithRetry(() => import('./views/CommunicationCenter/CommunicationCenterSettings'));
const CommunicationCenterSettingsPageView = lazyWithRetry(() => import('./views/CommunicationCenter/CommunicationCenterSettingsPage'));
const CustomFormsView = lazyWithRetry(() => import('./views/CustomForms'));
const CompaniesView = lazyWithRetry(() => import('./views/Companies'));
const MultiCompanyUsersView = lazyWithRetry(() => import('./views/MultiCompanyUsers'));
const CompanyGroupsView = lazyWithRetry(() => import('./views/CompanyGroups'));
const EmailExportView = lazyWithRetry(() => import('./views/EmailExport'));
const TransferCompanyDataView = lazyWithRetry(() => import('./views/TransferCompanyData'));

const HelpView = lazyWithRetry(() => import('./views/Help'));
const QuoteView = lazyWithRetry(() => import('./views/Pdf/Quote/index'));
const InvoiceView = lazyWithRetry(() => import('./views/Pdf/Invoice/index'));
const RoutesPdfView = lazyWithRetry(() => import('./views/Pdf/AllRoutes/index'));
const RoutePdfView = lazyWithRetry(() => import('./views/Pdf/Route/index'));
const RoutesExpandedPdfView = lazyWithRetry(() => import('./views/Pdf/RoutesExpandedRoutes'));
const MutilpleQuoteView = lazyWithRetry(() => import('./views/Pdf/MutilpleQuote/index'));
const MultipleInvoiceView = lazyWithRetry(() => import('./views/Pdf/MultipleInvoice/index'));
const BidsTodoView = lazyWithRetry(() => import('./views/Pdf/BidsTodo/index'));
const PriceLineItemsPdfView = lazyWithRetry(() => import('./views/Pdf/PriceLineItems/index'));
const SearchView = lazyWithRetry(() => import('./views/Search/index'));
const WorkorderView = lazyWithRetry(() => import('./views/Pdf/Workorder/index'));
const WorkorderHtmlView = lazyWithRetry(() => import('./views/Html/Workorder/index'));
const EmployeeTasksView = lazyWithRetry(() => import('./views/EmployeeTasks'));
const PaymentView = lazyWithRetry(() => import('./views/Payment'));
const TutorialsView = lazyWithRetry(() => import('./views/Tutorials'));

const PaymentRedirect = lazyWithRetry(() => import('./components/PaymentRedirect'));

const DisconnectView = lazyWithRetry(() => import('./views/Disconnect'));
const TermsOfServiceView = lazyWithRetry(() => import('./views/TermsOfService'));
const PrivacyPolicyView = lazyWithRetry(() => import('./views/PrivacyPolicy'));
const UnsubscribeView = lazyWithRetry(() => import('./views/Unsubscribe'));


const Routes = () => {

    const isMobile = useMediaQuery('(max-width: 780px)', { noSsr: true });

    return (
        <Suspense fallback={<LoadingFallback />}>
            <Switch>
                <Route
                    exact
                    path="/"
                    render={() => {
                        // Check if user is authenticated before redirecting
                        const token = localStorage.getItem("sw-token");
                        const isAuthenticated = token && JSON.parse(token)?.token && 
                            JSON.parse(token)?.expiresOn > Math.floor(new Date().getTime() / 1000);
                        
                        if (!isAuthenticated) {
                            return <Redirect to="/sign-in" />;
                        }
                        
                        // Role-based redirect for authenticated users
                        try {
                            const userRoles = JSON.parse(localStorage.getItem("sw-role"));
                            const parsedUserRoles = userRoles ? JSON.parse(userRoles) : [];
                            
                            // Field technicians should go to today's route, others to dashboard
                            const isFieldTechnician = parsedUserRoles.includes('Field Technician') ||
                                                    parsedUserRoles.includes('Field Technician & Estimator w/ Customer Access') ||
                                                    parsedUserRoles.includes('Field Technician & Estimator w/ Customer & Schedule Access');
                            
                            return isFieldTechnician ? 
                                <Redirect to="/employees/today-route" /> : 
                                <Redirect to="/dashboard" />;
                        } catch (error) {
                            // If there's an error parsing roles, default to dashboard
                            return <Redirect to="/dashboard" />;
                        }
                    }}
                />
                <RouteWithLayout
                    component={DashboardView}
                    exact
                    layout={MainLayout}
                    path="/dashboard"
                />
                <Route
                    component={SignInView}
                    exact
                    path="/sign-in"
                />
                <Route
                    component={OAuthSelectCompanyView}
                    exact
                    path="/oauth-select-company"
                />
                <Route
                    component={ForgotPasswordView}
                    exact
                    path="/sign-in/forgot-password"
                />
                <Route
                    component={ResetPasswordView}
                    exact
                    path="/sign-in/reset-password/:userId/:code"
                />
                <RouteWithLayout
                    component={EmployeeListView}
                    exact
                    layout={MainLayout}
                    path="/employees"
                />
                <RouteWithLayout
                    component={EmployeeDetailsView}
                    exact
                    layout={MainLayout}
                    path='/employees/:name/:id/:tab'
                />
                <RouteWithLayout
                    component={AddUserView}
                    exact
                    layout={MainLayout}
                    path="/employees/add-user"
                />
                <RouteWithLayout
                    component={EmployeesPayrollView}
                    exact
                    layout={MainLayout}
                    path='/employees/payroll-list'
                />
                <RouteWithLayout
                    component={EmployeesPayrollView}
                    exact
                    layout={MainLayout}
                    path='/employees/payroll-list/:start/:end'
                />
                <RouteWithLayout
                    component={EmployeePayrollView}
                    exact
                    layout={MainLayout}
                    path='/employees/payroll-detail/:name/:id/:startdate/:enddate'
                />
                <RouteWithLayout
                    component={EmployeeLogHoursView}
                    exact
                    layout={MainLayout}
                    path='/employees/log-hours'
                />
                <RouteWithLayout
                    component={EmployeeTasksView}
                    exact
                    layout={MainLayout}
                    path='/employees/tasks'
                />
                <RouteWithLayout
                    component={EmployeeLogHoursView}
                    exact
                    layout={MainLayout}
                    path='/employees/log-hours/:select_date'
                />
                <RouteWithLayout
                    component={EmployeeRoutesView}
                    exact
                    layout={MainLayout}
                    path='/employees/today-route'
                />
                <RouteWithLayout
                    component={AddCustomerView}
                    exact
                    layout={MainLayout}
                    path="/customers/add-customer"
                />
                <RouteWithLayout
                    component={AddCustomerView}
                    exact
                    layout={MainLayout}
                    path="/customers/add-customer/:id"
                />
                <RouteWithLayout
                    component={CustomerBillingView}
                    exact
                    layout={MainLayout}
                    path='/customers/billing/:customerId?'
                />
                <RouteWithLayout
                    component={SearchView}
                    exact
                    layout={MainLayout}
                    path='/customers/search'
                />
                <RouteWithLayout
                    component={CustomerView}
                    exact
                    layout={MainLayout}
                    path='/customers/:tab'
                />
                <RouteWithLayout
                    component={CustomerView}
                    exact
                    layout={MainLayout}
                    path='/customers/:tab/:select_date'
                />
                <RouteWithLayout
                    component={CustomerDetailsView}
                    exact
                    layout={MainLayout}
                    path='/customers/:name/:id/:tab'
                />
                <RouteWithLayout
                    component={CustomerJobsView}
                    exact
                    layout={MainLayout}
                    path='/customers/:name/:id/jobs/:jobId/:tab'
                />
                <RouteWithLayout
                    component={TemplateJobEditorView}
                    exact
                    layout={MainLayout}
                    path="/schedules/recurring-routes/templates/:templateId/jobs/:jobSnapshotId"
                />
                <RouteWithLayout
                    component={RecurringRoutesView}
                    exact
                    layout={MainLayout}
                    path="/schedules/recurring-routes"
                />
                <RouteWithLayout
                    component={SchedulesView}
                    exact
                    layout={MainLayout}
                    path="/schedules/:selectDate?"
                />
                <RouteWithLayout
                    component={isMobile ? RoutesMobileView : Routes2View}
                    exact
                    layout={MainLayout}
                    path="/schedules/routes/:date"
                />
                <RouteWithLayout
                    component={JobCompletionView}
                    exact
                    layout={MainLayout}
                    path='/job-completion'
                />
                <RouteWithLayout
                    component={ExpiredSchedulesView}
                    exact
                    layout={MainLayout}
                    path='/expired-schedules'
                />
                <RouteWithLayout
                    component={ReportView}
                    exact
                    layout={MainLayout}
                    path='/report'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin'
                />
                <RouteWithLayout
                    component={CustomFormsView}
                    exact
                    layout={MainLayout}
                    path='/admin/custom-forms'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/communications'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/contractsandforms'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/equipment'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/pricing'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/information'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/integrations'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/miscellaneous'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/smssettings'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/emailsettings'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/remindersettings'
                />

                <RouteWithLayout
                    component={CommunicationCenterView}
                    exact
                    layout={MainLayout}
                    path='/communication-center'
                />
                <RouteWithLayout
                    component={Advertising}
                    exact
                    layout={MainLayout}
                    path='/communication-center/advertising'
                />
                <RouteWithLayout
                    component={CommunicationCenterSettingsView}
                    exact
                    layout={MainLayout}
                    path='/communication-center/settings'
                />

                <RouteWithLayout
                    component={TutorialsView}
                    exact
                    layout={MainLayout}
                    path='/admin/tutorials'
                />
                <RouteWithLayout
                    component={CommunicationCenterSettingsPageView}
                    exact
                    layout={MainLayout}
                    path='/communication-center/:name/:id/settings'
                />
                <RouteWithLayout
                    component={CommunicationCenterView}
                    exact
                    layout={MainLayout}
                    path='/communication-center/:name/:id/:tab'
                />
                <RouteWithLayout
                    component={AdminView}
                    exact
                    layout={MainLayout}
                    path='/admin/:name/:id/:tab'
                />
                <RouteWithLayout
                    component={CompaniesView}
                    exact
                    layout={MainLayout}
                    path='/super-admin/companies'
                />
                <RouteWithLayout
                    component={MultiCompanyUsersView}
                    exact
                    layout={MainLayout}
                    path='/super-admin/multi-company-users'
                />
                <RouteWithLayout
                    component={CompanyGroupsView}
                    exact
                    layout={MainLayout}
                    path='/super-admin/company-groups'
                />
                <RouteWithLayout
                    component={EmailExportView}
                    exact
                    layout={MainLayout}
                    path='/super-admin/email-export'
                />
                <RouteWithLayout
                    component={TransferCompanyDataView}
                    exact
                    layout={MainLayout}
                    path='/super-admin/customer-transfer'
                />
                <RouteWithLayout
                    component={HelpView}
                    exact
                    layout={MainLayout}
                    path='/help'
                />
                <RouteWithLayout
                    component={SearchView}
                    exact
                    layout={MainLayout}
                    path='/search'
                />
                <RouteWithLayout
                    component={QuoteView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/quote/pdf/:id'
                />
                <AnonymousRoute
                    component={InvoiceView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/invoice/pdf/:id'
                />
                <RouteWithLayout
                    component={RoutesPdfView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/all-routes/pdf/:date'
                />
                <RouteWithLayout
                    component={RoutePdfView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/route/pdf/:id'
                />
                <RouteWithLayout
                    component={RoutesExpandedPdfView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/expanded-routes/pdf/:date'
                />
                <RouteWithLayout
                    component={MutilpleQuoteView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/multiple-quote/pdf/:id'
                />
                <RouteWithLayout
                    component={MultipleInvoiceView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/multiple-invoice/pdf/:id'
                />
                <RouteWithLayout
                    component={BidsTodoView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/bidtodo/pdf'
                />
                <RouteWithLayout
                    component={WorkorderView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/workorder/pdf/:id'
                />
                <RouteWithLayout
                    component={WorkorderHtmlView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/workorder/html/:id'
                />
                <RouteWithLayout
                    component={NotFoundView}
                    exact
                    layout={MinimalLayout}
                    path="/not-found"
                />
                <Route
                    exact
                    path="/payment/:encryptedPaymentInfo"
                    render={matchProps => (
                        <MainPaymentLayout>
                            <PaymentView {...matchProps} />
                        </MainPaymentLayout>
                    )}
                />
                <Route
                    component={PaymentRedirect}
                    exact
                    path="/pay/:shortLink"
                />
                <RouteWithLayout
                    component={DisconnectView}
                    exact
                    layout={MinimalPdfLayout}
                    path="/disconnect"
                />
                <RouteWithLayout
                    component={PrivacyPolicyView}
                    exact
                    layout={MinimalPdfLayout}
                    path="/PrivacyPolicy"
                />
                <RouteWithLayout
                    component={TermsOfServiceView}
                    exact
                    layout={MinimalPdfLayout}
                    path="/TermsOfService"
                />
                <RouteWithLayout
                    component={PriceLineItemsPdfView}
                    exact
                    layout={MinimalPdfLayout}
                    path='/price-line-items/pdf'
                />
                <Route
                    component={UnsubscribeView}
                    exact
                    path="/unsubscribe/:token"
                />
                <Redirect to="/not-found" />
            </Switch>
        </Suspense>
    );
};

export default Routes;
